import { toJS, action, observable, computed, makeObservable, runInAction } from 'mobx';

import {
  ApiBooking,
  ApiUpdateTravelerAssignmentRequest,
  ApiUpdateCustomerRequest,
  ApiTraveler,
  ApiAddItemRequest,
  ApiItem,
  ApiUpdateBookingItemSpecialRequest,
  ApiSpecialRequest,
  ApiErrorResponse,
  ApiErrorResponseFromJSON,
  ApiItemType,
  ApiBookedItem,
  ApiSeatMap,
  ApiComponent,
  ApiUpdateServiceNotesRequest,
  ApiNewsletterSubscriptionRequest,
  ApiTravelerComponentsAssociation,
  ApiPackageOffer,
  ApiPackageCart,
  ApiUpdateAffiliateRequest,
  ApiChangeComponentsRequest,
  ApiDiscount
} from '@ibe/api';

import ApiService from '../Api/ApiService';

import LoggerFactory from '../../Util/Logger/LoggerFactory';
import clone from '../../Util/clone';
import { bookingInitEvent } from '../../Events/commonEvents';
import MaskBookingService from './MaskBookingService';
import { default as AuthService } from '../Auth/AuthService';
import Headers from '../../Util/Headers';
import { AnalyticsService } from '../Analytics/AnalyticsService';

/* *
 *
 * BookingService is meant to manage one single booking
 *
 *       1:n         1:n
 *  User --> Booking --> Item
 *
 *  Booking == ShoppingCart
 *
 * */
class BookingService {
  private logger = LoggerFactory.get('BookingService');

  private ERR_NO_API = 'Can not init booking: No API instance.';

  private api: ApiService | null = null;

  private authService: AuthService | null = null;

  private isBusyValue = true;

  private hasErrorValue = false;

  private bookingValue: ApiBooking | null = null;

  private _seatMaps?: ApiSeatMap[] = undefined;

  private _selectablePackageOffers: ApiPackageOffer[];

  private _travelerComponentsAssociations: Array<ApiTravelerComponentsAssociation> | undefined;

  private _components: Array<ApiComponent> | undefined;

  private _packageCart?: ApiPackageCart | undefined = undefined;

  errorResponse: ApiErrorResponse | null = null;

  constructor() {
    makeObservable<
      BookingService,
      | 'isBusyValue'
      | 'hasErrorValue'
      | 'bookingValue'
      | 'apiCall'
      | 'apiCallWithBooking'
      | '_seatMaps'
      | '_travelerComponentsAssociations'
      | '_components'
      | '_selectablePackageOffers'
      | '_packageCart'
    >(this, {
      isBusyValue: observable,
      hasErrorValue: observable,
      bookingValue: observable,
      _travelerComponentsAssociations: observable,
      _components: observable,
      _selectablePackageOffers: observable,
      _packageCart: observable,
      errorResponse: observable,
      _seatMaps: observable,
      executeBookingAttempt: action,
      booking: computed,
      bookedItems: computed,
      discounts: computed,
      isBusy: computed,
      hasError: computed,
      init: action,
      addItem: action,
      addItems: action,
      removeItem: action,
      removeItems: action,
      doAttemptBooking: action,
      submitDiscount: action,
      doBooking: action,
      updateTravelerAssignment: action,
      updateCustomer: action,
      updateAffiliate: action,
      updateBookingItemSpecialRequest: action,
      addBestPricePackageToBooking: action,
      applyPaxRoomAssignments: action,
      addPromoCodesToBooking: action,
      addTraveler: action,
      updateTraveler: action,
      updateTravelers: action,
      removeTraveler: action,
      apiCall: action,
      apiCallWithBooking: action,
      getHotels: action,
      loadSeatMaps: action,
      loadSeatMap: action,
      loadPackageCart: action,
      selectItemsInPackageCart: action,
      updatePackageCartBooking: action,
      removeItemFromPackageCart: action,
      removeItemsFromPackageCart: action,
      reloadComponents: action,
      cleanInvalidDiscounts: action
    });
  }

  setApi(api: ApiService): void {
    this.api = api;
  }

  setAuth(auth: AuthService): void {
    this.authService = auth;
  }

  setNewBooking(booking: ApiBooking): void {
    this.bookingValue = booking;
  }

  clearBooking(): void {
    this.bookingValue = null;
  }

  cleanInvalidDiscounts(): void {
    if (this.bookingValue?.discounts && this.bookingValue?.discounts.length > 0) {
      this.bookingValue.discounts = this.bookingValue.discounts.filter(
        (discount: ApiDiscount) => !discount.isInvalidated
      );
    }
  }

  async executeBookingAttempt(): Promise<void> {
    if (!this.api) {
      throw new Error(this.ERR_NO_API);
    }

    this.errorResponse = null;

    if (this.booking && this.booking.bookedItems.length === 0) {
      return Promise.resolve();
    }

    if (this.booking) {
      this.isBusyValue = true;
      try {
        this.bookingValue = await this.api.doAttemptBooking(this.booking.id);
        this.isBusyValue = false;
        return Promise.resolve();
      } catch (e) {
        try {
          this.hasErrorValue = true;
          this.errorResponse = ApiErrorResponseFromJSON(await (e as Response).json());
        } catch (ex) {
          this.logger.error(ex);
        }

        this.isBusyValue = false;
        return Promise.reject(e);
      }
    }

    return Promise.reject();
  }

  get booking(): ApiBooking | null {
    return this.bookingValue ? (clone(this.bookingValue) as ApiBooking) : null;
  }

  get bookedItems(): Array<ApiBookedItem> | null {
    return this.bookingValue?.bookedItems
      ? (clone(this.bookingValue?.bookedItems) as Array<ApiBookedItem>)
      : null;
  }

  get discounts(): ApiDiscount[] | undefined {
    return this.bookingValue?.discounts
      ? (clone(this.bookingValue?.discounts) as ApiDiscount[])
      : undefined;
  }

  get isBusy(): boolean {
    return this.isBusyValue;
  }

  get hasError(): boolean {
    return this.hasErrorValue;
  }

  async init(
    bookingId?: string,
    throwError?: boolean,
    packageId?: string,
    packageCartId?: string
  ): Promise<void> {
    return await this.apiCall(async api => {
      if (bookingId) {
        if (packageId && packageCartId) {
          try {
            await this.loadPackageCart(packageCartId);
            this.bookingValue = await api.getBooking(bookingId);
          } catch (err) {
            throw new Error(err as string);
          }
        } else if (packageId) {
          try {
            await this.initPackageCart(packageId, bookingId, packageCartId);
          } catch (err) {
            throw new Error(err as string);
          }
        } else {
          this.bookingValue = await api.getBooking(bookingId);
          this.logger.log('getBooking', toJS(this.bookingValue));
        }
      } else {
        if (packageId) {
          try {
            await this.initPackageCart(packageId, 'EMPTY', packageCartId);
            if (this._packageCart?.bookingId) {
              this.bookingValue = await api.getBooking(this._packageCart?.bookingId);
            } else {
              this.bookingValue = await api.initBooking();
            }
          } catch (err) {
            throw new Error(err as string);
          }
        } else {
          if (this.authService) {
            const response = await api.initBookingRaw();

            const token = response.raw.headers.get(Headers.AUTHORIZATION);
            if (token) {
              this.authService.authenticate(token);
            }
            this.bookingValue = await response.value();
          } else {
            this.bookingValue = await api.initBooking();
          }
        }

        this.logger.log('initBooking', toJS(this.bookingValue));
      }
      bookingInitEvent.broadcast({
        booking: MaskBookingService.maskBooking(this.bookingValue),
        item: null,
        analytics: AnalyticsService.createAnalyticsObject(this.bookingValue)
      });
    }, throwError);
  }

  async addItem(itemId: string, options?: ApiAddItemRequest, bookingId?: string): Promise<void> {
    await this.apiCallWithBooking(async (booking, api) => {
      if (booking && booking.id) {
        this.bookingValue = await api.addItemToBooking(booking.id, itemId, options);
      } else {
        this.logger.error('booking invalid: ', booking);
      }
    }, bookingId);
  }

  async addItems(itemsMap: { [key: string]: ApiAddItemRequest[] }): Promise<void> {
    await this.apiCallWithBooking(async (booking, api) => {
      if (booking && booking.id) {
        this.bookingValue = await api.addItemsToBooking(booking.id, itemsMap);
      } else {
        this.logger.error('booking invalid: ', booking);
      }
    });
  }

  async removeItem(itemId: string): Promise<void> {
    await this.apiCallWithBooking(async (booking, api) => {
      if (booking && booking.id) {
        this.bookingValue = await api.deleteItemFromBooking(booking.id, itemId);
      } else {
        this.logger.error('booking invalid: ', booking);
      }
    });
  }

  async removeItems(itemIds: string[]): Promise<void> {
    await this.apiCallWithBooking(async (booking, api) => {
      if (booking && booking.id) {
        this.bookingValue = await api.deleteItemsFromBooking(booking.id, itemIds);
      } else {
        this.logger.error('booking invalid: ', booking);
      }
    });
  }

  async doAttemptBooking(): Promise<void> {
    await this.apiCallWithBooking(async (booking, api) => {
      if (booking && booking.id) {
        this.logger.log('doAttemptBooking', booking.id);
        this.bookingValue = await api.doAttemptBooking(booking.id);
      } else {
        this.logger.error('booking invalid: ', booking);
      }
    });
  }

  async submitDiscount(discountCode: string): Promise<void> {
    await this.apiCallWithBooking(async (booking, api) => {
      if (booking && booking.id) {
        this.bookingValue = await api.submitDiscount(booking.id, { discountCode: discountCode });
      } else {
        this.logger.error('booking invalid while trying to submit discount code: ', booking);
      }
    });
  }

  async doBooking(): Promise<void> {
    await this.apiCallWithBooking(async (booking, api) => {
      if (booking && booking.id) {
        this.logger.log('doBooking', booking.id);
        this.bookingValue = await api.doBooking(booking.id);
      } else {
        this.logger.error('booking invalid: ', booking);
      }
    });
  }

  async updateTravelerAssignment(options: ApiUpdateTravelerAssignmentRequest): Promise<void> {
    await this.apiCallWithBooking(async (booking, api) => {
      if (booking && booking.id) {
        this.bookingValue = await api.updateTravelerAssignment(booking.id, options);
      } else {
        this.logger.error('booking invalid: ', booking);
      }
    });
  }

  @action
  async updateBookingItemSpecialRequest(
    specialRequests: ApiSpecialRequest[],
    itemId: string
  ): Promise<void> {
    await this.apiCallWithBooking(async (booking, api) => {
      if (booking && booking.id) {
        await api.updateBookingItemSpecialRequest(booking.id, {
          itemId,
          specialRequests
        } as ApiUpdateBookingItemSpecialRequest);
      } else {
        this.logger.error('update booking special requests failed: ', booking);
      }
    });
  }

  @action
  async loadSeatMaps(itemIds: string[]): Promise<void> {
    try {
      this._seatMaps = (await this.api?.getSeatMaps({ itemIds }))?.seatMaps;
    } catch (e) {
      this.logger.error(e);
      this.hasErrorValue = true;
      this.errorResponse = ApiErrorResponseFromJSON(await (e as Response).json());
    }
  }

  @action
  async loadSeatMap(itemId: string): Promise<void> {
    try {
      const seatMap = (await this.api?.getSeatMap({ itemId }))?.seatMap;
      if (seatMap) {
        this._seatMaps = [seatMap];
      }
    } catch (e) {
      this.logger.error(e);
      this.hasErrorValue = true;
      this.errorResponse = ApiErrorResponseFromJSON(await (e as Response).json());
    }
  }

  @computed get seatMaps(): ApiSeatMap[] | undefined {
    return this._seatMaps;
  }

  async updateCustomer(request: ApiUpdateCustomerRequest): Promise<void> {
    await this.apiCallWithBooking(async (booking, api) => {
      this.logger.log('updateCustomer', request.customer);
      if (booking && booking.id) {
        this.bookingValue = await api.updateCustomer(booking.id, request);
      } else {
        this.logger.error('booking invalid: ', booking);
      }
    });
  }

  async updateAffiliate(request: ApiUpdateAffiliateRequest): Promise<void> {
    await this.apiCallWithBooking(async (booking, api) => {
      this.logger.log('updateAffiliate', request.affiliateNumber);
      if (booking && booking.id) {
        this.bookingValue = await api.updateAffiliate(booking.id, request);
      } else {
        this.logger.error('booking invalid: ', booking);
      }
    });
  }

  async addTraveler(traveler: ApiTraveler): Promise<void> {
    await this.apiCallWithBooking(async (booking, api) => {
      this.logger.log('addTraveler', traveler);
      if (booking && booking.id) {
        this.bookingValue = await api.addTraveler(booking.id, false, traveler);
      } else {
        this.logger.error('booking invalid: ', booking);
      }
    });
  }

  async addPromoCodesToBooking(promoCodes: string[]): Promise<void> {
    await this.apiCallWithBooking(async (booking, api) => {
      if (booking && booking.id) {
        this.bookingValue = await api.addPromoCodes(booking.id, promoCodes);
      } else {
        this.logger.error('booking invalid: ', booking);
      }
    });
  }

  async addBestPricePackageToBooking(
    packageModelId: string,
    packageDetailId: string
  ): Promise<void> {
    try {
      await this.apiCallWithBooking(async (booking, api) => {
        if (booking && booking.id) {
          this.bookingValue = await api.addBestPricePackageToBooking(
            packageModelId,
            packageDetailId,
            booking.id
          );
        } else {
          this.logger.error('booking invalid: ', booking);
        }
      });
    } catch (e) {
      this.hasErrorValue = true;
      return Promise.resolve();
    }
  }

  async applyPaxRoomAssignments(components: ApiComponent[]): Promise<void> {
    await this.apiCallWithBooking(async (booking, api) => {
      this.logger.log('updateTravelers', components);
      if (booking && booking.id) {
        const res = await api.applyPaxRoomAssignments(booking.id, { components });
        this.bookingValue = res.booking;
      } else {
        this.logger.error('booking invalid: ', booking);
      }
    });
  }

  async updateTraveler(traveler: ApiTraveler): Promise<void> {
    await this.apiCallWithBooking(async (booking, api) => {
      this.logger.log('updateTraveler', traveler);
      if (booking && booking.id) {
        this.bookingValue = await api.updateTraveler(booking.id, false, traveler);
      } else {
        this.logger.error('booking invalid: ', booking);
      }
    });
  }

  async updateTravelers(travelers: ApiTraveler[]): Promise<void> {
    await this.apiCallWithBooking(async (booking, api) => {
      this.logger.log('updateTravelers', travelers);
      if (booking && booking.id) {
        this.bookingValue = await api.updateTravelers(booking.id, false, travelers);
      } else {
        this.logger.error('booking invalid: ', booking);
      }
    });
  }

  async removeTraveler(traveler: ApiTraveler): Promise<void> {
    await this.apiCallWithBooking(async (booking, api) => {
      this.logger.log('removeTraveler', traveler);
      if (booking && booking.id) {
        this.bookingValue = await api.removeTraveler(booking.id, false, traveler);
      } else {
        this.logger.error('booking invalid: ', booking);
      }
    });
  }

  async updateServiceNotes(
    rq: ApiUpdateServiceNotesRequest,
    attempt: boolean
  ): Promise<ApiBooking> {
    if (!this.api) {
      throw new Error(this.ERR_NO_API);
    }
    try {
      if (rq) {
        const response = await this.api.updateServiceNotes(attempt, rq);
        this.bookingValue = response;
        return response;
      }
    } catch (e) {
      this.logger.error('Update service notes failed');
    }
    return Promise.reject();
  }

  private async apiCall(
    apiCall: (api: ApiService) => Promise<void>,
    throwError?: boolean
  ): Promise<void> {
    if (!this.api) {
      throw new Error(this.ERR_NO_API);
    }

    // these are observables --> UI will "play" changes sequentially
    this.isBusyValue = true;
    try {
      await apiCall(this.api);
      this.hasErrorValue = false;
      this.errorResponse = null;
    } catch (e) {
      this.logger.error(e);
      this.hasErrorValue = true;
      this.errorResponse = ApiErrorResponseFromJSON(await (e as Response).json());
      if (throwError) {
        throw e;
      }
    } finally {
      this.isBusyValue = false;
    }
  }

  private async apiCallWithBooking(
    apiCall: (booking: ApiBooking | null, api: ApiService) => Promise<void>,
    bookingId?: string
  ): Promise<void> {
    if (!this.bookingValue || !this.bookingValue.id) {
      await this.init(bookingId);
    }

    return this.apiCall(async api => apiCall(this.booking, api));
  }

  static getTraveler(travellers: ApiTraveler[], personId: string): ApiTraveler | undefined {
    return travellers.find(traveler => traveler.id === personId);
  }

  getItem(itemId: string): ApiItem | null {
    if (this.booking) {
      const parentItem = this.booking.items.find(item => item.id === itemId);
      if (parentItem) {
        return parentItem;
      }
    }
    return null;
  }

  getHotels(): ApiBookedItem[] {
    const hotels: ApiBookedItem[] = [];

    if (this.booking) {
      this.booking.bookedItems.forEach(item => {
        if (ApiItemType.HOTEL === item.itemType) {
          hotels.push(item);
        }
      });
    }

    return hotels;
  }

  async makeNewsletterSubscription(
    newsletterSubscriptionRequest: ApiNewsletterSubscriptionRequest
  ): Promise<void> {
    if (this.booking && this.api) {
      try {
        return await this.api.newsletterSubscription(newsletterSubscriptionRequest);
      } catch (e) {
        this.errorResponse = ApiErrorResponseFromJSON(await (e as Response).json());
      }
    }
    return Promise.reject();
  }

  async initPackageCart(
    packageId: string,
    bookingId: string,
    packageCartId?: string
  ): Promise<void> {
    return await this.apiCall(async api => {
      const cart: ApiPackageCart = await api.start(packageId, bookingId, true, packageCartId);
      runInAction(() => {
        if (!!cart) {
          this._packageCart = cart;
        }
      });
    }, true);
  }

  async loadPackageCart(packageCartId: string): Promise<void> {
    return await this.apiCall(async api => {
      const cart: ApiPackageCart = await api.load(packageCartId);
      runInAction(() => {
        if (!!cart) {
          this._packageCart = cart;
        }
      });
    }, true);
  }

  async updatePackageCartBooking(): Promise<void> {
    return await this.apiCall(async api => {
      if (this._packageCart) {
        const cart: ApiPackageCart = await api.attemptBooking(this._packageCart.id);
        const booking: ApiBooking = await api.getBooking(this._packageCart.bookingId);
        runInAction(() => {
          if (!!cart) {
            this._packageCart = cart;
          }
          if (!!booking) {
            this.bookingValue = booking;
          }
        });
      }
    }, true);
  }

  async removeItemFromPackageCart(componentId: string, removedItemId: string): Promise<void> {
    return await this.apiCall(async api => {
      if (this._packageCart) {
        const cart: ApiPackageCart = await api.removeItem(
          this._packageCart.id,
          componentId,
          removedItemId
        );
        runInAction(() => {
          if (!!cart) {
            this._packageCart = cart;
          }
        });
      }
    }, true);
  }

  async removeItemsFromPackageCart(componentId: string, removedItemIds: string[]): Promise<void> {
    return await this.apiCall(async api => {
      if (this._packageCart) {
        const cart: ApiPackageCart = await api.removeItems(this._packageCart.id, componentId, {
          removedItemIds
        });
        runInAction(() => {
          if (!!cart) {
            this._packageCart = cart;
          }
        });
      }
    }, true);
  }

  async selectItemsInPackageCart(
    componentId: string,
    selectedItemsIds: string[],
    bookingOptions: { [key: string]: ApiAddItemRequest }
  ): Promise<void> {
    return await this.apiCall(async api => {
      if (this._packageCart) {
        const cart: ApiPackageCart = await api.selectItems(this._packageCart?.id, componentId, {
          bookingOptions,
          selectedItemIds: selectedItemsIds
        });
        runInAction(() => {
          if (!!cart) {
            this._packageCart = cart;
          }
        });
      }
    }, true);
  }

  public updateSelectablePackageOffers(selectablePackageOffers: ApiPackageOffer[]): void {
    this._selectablePackageOffers = selectablePackageOffers;
  }

  public updateTravelerComponentsAssociations(
    travelerComponentsAssociations: Array<ApiTravelerComponentsAssociation>
  ): void {
    this._travelerComponentsAssociations = travelerComponentsAssociations;
  }

  public updateComponents(components: Array<ApiComponent>): void {
    this._components = components;
  }

  get selectablePackageOffers(): ApiPackageOffer[] {
    return this._selectablePackageOffers;
  }

  get components(): Array<ApiComponent> | undefined {
    return this._components;
  }

  get travelerComponentsAssociations(): Array<ApiTravelerComponentsAssociation> | undefined {
    return this._travelerComponentsAssociations;
  }

  get packageCart(): ApiPackageCart | undefined {
    return this._packageCart;
  }

  async reloadComponents(request: ApiChangeComponentsRequest): Promise<void> {
    return await this.apiCall(async api => {
      if (this._packageCart && this._packageCart.packageModel.packageDetails) {
        const cart: ApiPackageCart = await api.changeComponents(
          this._packageCart.id,
          this._packageCart.packageModel.packageDetails?.[0].id,
          request
        );
        runInAction(() => {
          if (!!cart) {
            this._packageCart = cart;
          }
        });
      }
    }, true);
  }
}

export default BookingService;
